'use client';

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase, supabaseUrl } from '@/lib/supabaseClient';

type InviteResult = {
  userId: string | null;
  error: string | null;
};

export function useInvitarTrabajador() {
  const empresaId = localStorage.getItem('id_empresa');
  const idUsuario = localStorage.getItem('id_usuario');

  const [loading, setLoading] = useState(false);

  const invitar = useCallback(
    async (
      email: string,
      fullName: string,
      intervaloCita: number,
      activo: boolean,
      servicios: any[] = []
    ): Promise<InviteResult> => {
      setLoading(true);

      try {
        if (!empresaId || !idUsuario) {
          return { userId: null, error: 'Empresa o usuario no encontrado' };
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          return { userId: null, error: 'Sesión no válida' };
        }

        const res = await fetch(`${supabaseUrl}/functions/v1/invite-worker`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            email,
            fullName,
            intervaloCita,
            activo,
            empresaId,
            usuarioSistemaId: idUsuario,
            servicios,
          }),
        });

        const data = await res.json();

        if (!res.ok) {
          const err = new Error(data?.error ?? 'Error al invitar');
          Sentry.captureException(err, { extra: { email, empresaId } });
          return { userId: null, error: err.message };
        }

        return { userId: data.userId, error: null };
      } catch (e: any) {
        Sentry.captureException(e, { extra: { email, empresaId } });
        return { userId: null, error: e?.message ?? 'Error desconocido' };
      } finally {
        setLoading(false);
      }
    },
    [empresaId, idUsuario]
  );

  return { invitar, loading };
}

/* ============================================================
 * EDITAR TRABAJADOR
 * ============================================================ */

export async function editarTrabajador(
  userId: string,
  fullName: string,
  intervaloCita: number,
  activo: boolean,
  idEmpresa: string,
  idUsuarioSistema: string,
  idPerfil: string,
  autocompletarCitas: boolean,
  autocompletarHoras: number,
  autocompletarDesde: string | null,
  autocompletarTipoPago: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('Sesión no válida');
    }

    const res = await fetch(`${supabaseUrl}/functions/v1/editar-trabajador`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ userId, fullName }),
    });

    const fnData = await res.json();

    if (!res.ok) {
      throw new Error(fnData?.error ?? 'Error actualizando nombre');
    }

    const { error } = await supabase
      .from('usuarios_perfiles')
      .update({
        intervalo_cita: intervaloCita,
        activo,
        autocompletar_citas: autocompletarCitas,
        autocompletar_horas: autocompletarHoras,
        autocompletar_desde: autocompletarDesde,
        autocompletar_tipo_pago: autocompletarTipoPago,
        usuario_edicion: idUsuarioSistema,
      })
      .eq('id_usuario', userId)
      .eq('id_empresa', idEmpresa)
      .eq('id_perfil', idPerfil);

    if (error) throw error;

    return { ok: true };
  } catch (e: any) {
    Sentry.captureException(e, { extra: { userId, idEmpresa } });
    return { ok: false, error: e?.message ?? 'Error desconocido' };
  }
}

export function useReenviarInvitacion() {
  const empresaId = localStorage.getItem('id_empresa');
  const idUsuario = localStorage.getItem('id_usuario');
  const [loading, setLoading] = useState(false);

  const reenviar = useCallback(
    async (worker: {
      email: string;
      nombre_completo: string;
      intervalo_cita?: number;
      activo?: boolean;
    }): Promise<{ ok: boolean; error?: string }> => {
      setLoading(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          return { ok: false, error: 'Sesión no válida' };
        }

        const res = await fetch(`${supabaseUrl}/functions/v1/invite-worker`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            email: worker.email,
            fullName: worker.nombre_completo,
            intervaloCita: worker.intervalo_cita ?? 30,
            activo: worker.activo ?? true,
            empresaId,
            usuarioSistemaId: idUsuario,
            servicios: [],
            resend: true,
          }),
        });

        const data = await res.json();
        if (!res.ok) return { ok: false, error: data?.error ?? 'Error al reenviar' };
        return { ok: true };
      } catch (e: any) {
        return { ok: false, error: e?.message ?? 'Error desconocido' };
      } finally {
        setLoading(false);
      }
    },
    [empresaId, idUsuario]
  );

  return { reenviar, loading };
}